/**
 * Intelligent Model Selector
 *
 * Analyzes task characteristics and dynamically selects the best model
 * based on requirements (speed, quality, language, privacy, cost).
 *
 * Note: This module does NOT access secrets directly. The caller must
 * provide availableProviders to specify which providers have API keys.
 */

import {
  getAllModels,
  filterModels,
  getModelMetadata,
} from './model-metadata.js'

// Module-level state for available providers (set via setAvailableProviders)
let configuredProviders = null

/**
 * Analyze prompt to extract task characteristics
 * @param {string} prompt - User's natural language prompt
 * @param {Array} conversationHistory - Previous conversation context
 * @returns {Object} Task characteristics
 */
function analyzeTaskCharacteristics(prompt, conversationHistory = []) {
  const promptLower = prompt.toLowerCase()
  const promptLength = prompt.length
  const historyLength = conversationHistory.length

  // Detect language
  const hasChinese = /[\u4e00-\u9fa5]/.test(prompt)
  const hasKorean = /[\uac00-\ud7af]/.test(prompt)
  const hasJapanese = /[\u3040-\u309f\u30a0-\u30ff]/.test(prompt)
  const isAsianLanguage = hasChinese || hasKorean || hasJapanese
  const language = hasChinese ? 'chinese' : 'english'

  // Detect complexity indicators
  const complexityKeywords = [
    'analyze',
    'explain',
    'compare',
    'evaluate',
    'reasoning',
    'complex',
    'detailed',
    'comprehensive',
    '分析',
    '解释',
    '比较',
    '评估',
    '推理',
    '复杂',
    '详细',
  ]
  const hasComplexity = complexityKeywords.some((kw) =>
    promptLower.includes(kw)
  )
  const isComplex = hasComplexity || promptLength > 500

  // Detect speed requirements
  const speedKeywords = [
    'quick',
    'fast',
    'rapid',
    'immediate',
    'urgent',
    'asap',
    '快速',
    '立即',
    '马上',
    '紧急',
  ]
  const needsSpeed = speedKeywords.some((kw) => promptLower.includes(kw))

  // Detect code generation
  const codeKeywords = [
    'code',
    'program',
    'function',
    'script',
    'implement',
    'debug',
    '代码',
    '编程',
    '函数',
    '实现',
    '调试',
  ]
  const isCodeTask = codeKeywords.some((kw) => promptLower.includes(kw))

  // Detect privacy requirements
  const privacyKeywords = [
    'private',
    'confidential',
    'secure',
    'offline',
    'local',
    '隐私',
    '保密',
    '离线',
    '本地',
  ]
  const needsPrivacy = privacyKeywords.some((kw) => promptLower.includes(kw))

  // Detect creative tasks
  const creativeKeywords = [
    'creative',
    'write',
    'story',
    'poem',
    'brainstorm',
    'idea',
    '创意',
    '创作',
    '写作',
    '故事',
    '灵感',
  ]
  const isCreative = creativeKeywords.some((kw) => promptLower.includes(kw))

  // Detect long context
  const hasLongContext = historyLength > 10 || promptLength > 1000

  return {
    language,
    isAsianLanguage,
    hasChinese,
    isComplex,
    needsSpeed,
    isCodeTask,
    needsPrivacy,
    isCreative,
    hasLongContext,
    promptLength,
    historyLength,
  }
}

/**
 * Calculate model score based on task characteristics
 * @param {Object} model - Model metadata
 * @param {Object} characteristics - Task characteristics
 * @param {Object} options - Selection options
 * @returns {number} Score (0-100)
 */
function calculateModelScore(model, characteristics, options) {
  const {
    language,
    hasChinese,
    isComplex,
    needsSpeed,
    needsPrivacy,
    hasLongContext,
  } = characteristics

  const { prioritizeCost, prioritizeSpeed, prioritizeQuality } = options

  let score = 50 // Base score

  // Language match bonus
  if (hasChinese) {
    score += model.performance.chineseScore * 3 // Chinese score is critical
  } else {
    score += model.performance.englishScore * 2
  }

  // Speed requirements
  if (needsSpeed) {
    score += model.performance.speedScore * 4 // Speed is very important
  } else {
    score += model.performance.speedScore * 1.5 // Still somewhat important
  }

  // Quality requirements
  if (isComplex) {
    score += model.performance.qualityScore * 4 // Quality critical for complex tasks
  } else {
    score += model.performance.qualityScore * 2
  }

  // Privacy requirements
  if (needsPrivacy && model.cost.local) {
    score += 20 // Big bonus for local models
  } else if (needsPrivacy && !model.cost.local) {
    score -= 15 // Penalty for cloud models when privacy needed
  }

  // Free/local models get a default bonus (prefer zero-cost inference)
  if (model.cost.free) score += 5
  if (model.cost.local) score += 5

  // Additional cost considerations when explicitly prioritizing cost
  if (prioritizeCost) {
    if (model.cost.free) {
      score += 15
    } else if (model.cost.inputPer1MTokens < 1.0) {
      score += 10
    } else {
      score -= model.cost.inputPer1MTokens * 2
    }
  }

  // Long context support
  if (hasLongContext) {
    const contextScore = Math.min(
      model.limits.contextWindow / 100000,
      10
    )
    score += contextScore * 2
  }

  // Priority adjustments
  if (prioritizeSpeed) {
    score += model.performance.speedScore * 3
  }
  if (prioritizeQuality) {
    score += model.performance.qualityScore * 3
  }

  // Tool calling capability (always important for agent)
  if (model.capabilities.toolCalling) {
    score += 10
  }

  return Math.max(0, Math.min(100, score))
}

/**
 * Set available providers (should be called during SDK initialization)
 * @param {string[]} providers - Array of provider names that have API keys
 */
export function setAvailableProviders(providers) {
  if (!Array.isArray(providers)) {
    throw new Error('providers must be an array')
  }
  configuredProviders = new Set(providers)
}

/**
 * Get available models based on configured providers
 * @param {string[]} [providers] - Optional providers override
 * @returns {Array} Available models
 */
function getAvailableModels(providers) {
  let models = getAllModels()

  // Use provided providers, or configured providers, or all providers
  const availableSet = providers
    ? new Set(providers)
    : configuredProviders

  // Filter to only models we have access to
  models = models.filter((model) => {
    // Ollama models are always available if service is running
    if (model.provider === 'ollama') return true

    // If no providers configured, assume all are available
    if (!availableSet) return true

    // Cloud providers need to be in the available set
    return availableSet.has(model.provider)
  })

  return models
}

/**
 * Select best model for the task
 * @param {string} prompt - User's prompt
 * @param {Object} options - Selection options
 * @param {Array} options.conversationHistory - Previous conversation
 * @param {string[]} options.availableProviders - Providers with API keys configured
 * @param {boolean} options.prioritizeCost - Prioritize cost over quality
 * @param {boolean} options.prioritizeSpeed - Prioritize speed over quality
 * @param {boolean} options.prioritizeQuality - Prioritize quality over speed
 * @param {boolean} options.requireLocal - Require local model (privacy)
 * @param {number} options.maxCostPer1M - Maximum cost per 1M tokens
 * @returns {Object} { provider, model, reason }
 */
export function selectBestModel(prompt, options = {}) {
  const {
    conversationHistory = [],
    availableProviders,
    prioritizeCost = false,
    prioritizeSpeed = false,
    prioritizeQuality = false,
    requireLocal = false,
    maxCostPer1M = Infinity,
  } = options

  // Analyze task characteristics
  const characteristics = analyzeTaskCharacteristics(prompt, conversationHistory)

  // Get available models (filtered by available providers)
  let availableModels = getAvailableModels(availableProviders)

  if (availableModels.length === 0) {
    throw new Error(
      'No models available. Please configure API keys or install Ollama.'
    )
  }

  // Apply hard filters
  if (requireLocal || characteristics.needsPrivacy) {
    availableModels = availableModels.filter((m) => m.cost.local)
  }

  if (maxCostPer1M < Infinity) {
    availableModels = availableModels.filter(
      (m) => m.cost.inputPer1MTokens <= maxCostPer1M
    )
  }

  // Must support tool calling
  availableModels = availableModels.filter((m) => m.capabilities.toolCalling)

  if (availableModels.length === 0) {
    throw new Error('No models match the specified requirements')
  }

  // Calculate scores for each model
  const scoredModels = availableModels.map((model) => ({
    model,
    score: calculateModelScore(model, characteristics, options),
  }))

  // Sort by score (highest first)
  scoredModels.sort((a, b) => b.score - a.score)

  // Select best model
  const best = scoredModels[0]
  const selectedModel = best.model

  // Generate selection reason
  const reasons = []

  if (characteristics.hasChinese) {
    reasons.push(
      `Chinese language (score: ${selectedModel.performance.chineseScore}/10)`
    )
  }

  if (characteristics.needsSpeed || prioritizeSpeed) {
    reasons.push(
      `Speed requirement (score: ${selectedModel.performance.speedScore}/10)`
    )
  }

  if (characteristics.isComplex || prioritizeQuality) {
    reasons.push(
      `Quality requirement (score: ${selectedModel.performance.qualityScore}/10)`
    )
  }

  if (characteristics.needsPrivacy || requireLocal) {
    reasons.push('Privacy/local requirement')
  }

  if (selectedModel.cost.free) {
    reasons.push('Cost-efficient (free)')
  }

  const reason = reasons.length > 0 ? reasons.join(', ') : 'Best overall match'

  return {
    provider: selectedModel.provider,
    model: selectedModel.modelId,
    reason,
    characteristics,
    score: best.score.toFixed(1),
    alternatives: scoredModels.slice(1, 3).map((s) => ({
      provider: s.model.provider,
      model: s.model.modelId,
      name: s.model.name,
      score: s.score.toFixed(1),
    })),
  }
}

/**
 * Helper function to find best model by criteria
 * @param {Function} filterFn - Additional filter function
 * @param {Function} sortFn - Sort comparison function
 * @param {string[]} [availableProviders] - Providers with API keys
 * @returns {Object|null} { provider, model } or null
 */
function findBestModel(filterFn, sortFn, availableProviders) {
  const models = getAvailableModels(availableProviders)
  const filtered = models.filter(
    (m) => m.capabilities.toolCalling && (!filterFn || filterFn(m))
  )
  if (filtered.length === 0) return null

  const best = filtered.sort(sortFn)[0]
  return { provider: best.provider, model: best.modelId }
}

/**
 * Quick model recommendation shortcuts
 */
export const modelPresets = {
  // Fast response for simple queries
  fast: () =>
    findBestModel(
      null,
      (a, b) => b.performance.speedScore - a.performance.speedScore
    ),

  // High quality for complex reasoning
  quality: () =>
    findBestModel(
      null,
      (a, b) => b.performance.qualityScore - a.performance.qualityScore
    ),

  // Free models only
  free: () =>
    findBestModel(
      (m) => m.cost.free,
      (a, b) => b.performance.qualityScore - a.performance.qualityScore
    ),

  // Local/private models only
  local: () =>
    findBestModel(
      (m) => m.cost.local,
      (a, b) => b.performance.qualityScore - a.performance.qualityScore
    ),

  // Best for Chinese
  chinese: () =>
    findBestModel(
      null,
      (a, b) => b.performance.chineseScore - a.performance.chineseScore
    ),
}
